import * as Cesium from 'cesium';
import { useSelectById } from 'hooks';
import _ from 'lodash';
import { useModel } from 'middleware/SatelliteApi/template';
import { memo, useMemo, useRef } from 'react';
import {
  Camera,
  Entity,
  Globe,
  LabelGraphics,
  PointPrimitive,
  PointPrimitiveCollection,
  PolylineGraphics,
  Scene,
  ScreenSpaceCameraController,
  ShadowMap,
  SkyAtmosphere,
  SkyBox,
  Sun,
  Viewer,
} from 'resium';
import { AgentVables } from 'utils/vable';
import {
  cesiumModelScaleDown,
  cesiumScaleLinearIntercept,
  cesiumScaleLinearSlope,
} from '../PlaybackWGroups/general/constants';

const configMap = {
  TemplatedAgent: {
    colors: {
      primary: Cesium.Color.LIMEGREEN,
      tail: Cesium.Color.fromAlpha(Cesium.Color.LIMEGREEN, 0.5),
      path: Cesium.Color.fromAlpha(Cesium.Color.GOLDENROD, 0.5),
    },
    pixelSize: 10,
    width: 2,
  },
  PeripheralSpacePoint: {
    colors: {
      primary: Cesium.Color.RED,
      tail: Cesium.Color.fromAlpha(Cesium.Color.TOMATO, 0.5),
    },
    pixelSize: 7,
    width: 1,
  },
  PeripheralGroundPoint: {
    colors: {
      primary: Cesium.Color.MAGENTA,
    },
    pixelSize: 7,
  },
};

const ScenarioAgent = ({ agent, viewerRef }) => {
  const agentTemplate = useSelectById('MissionVersion', agent.templateRef);
  const config = configMap[agent.type];

  const [pos, orbit, path] = useMemo(() => {
    let initialPos = [];
    let orbit;
    let path;
    // Ground Agent
    if (agent.type === 'PeripheralGroundPoint') {
      initialPos = [agent.lon.deg, agent.lat.deg, 0];
    }
    // Terrestrial Agent
    else if (agent.kinematics.type in AgentVables.WaypointType) {
      const _initialPos = agent.kinematics.waypoints[0]?.llaDeg || [38.8812, -77.111315, 0.1];
      initialPos = [_initialPos[1], _initialPos[0], _initialPos[2]];
      path = agent.kinematics.waypoints.map((waypoint) => {
        const [lat, long, alt] = waypoint.llaDeg;
        return Cesium.Cartesian3.fromDegrees(long, lat, alt);
      });
    }
    // Space Agent
    else {
      initialPos = [
        Math.random() * 360 - 180,
        Math.random() * 60 - 30,
        Math.random() * 2000000 + 1000000,
      ];
      const latOffset = Math.random() * 4 + 1;
      const longOffset = Math.random() * 4 + 1;
      const pos = new Cesium.Cartesian3.fromDegrees(initialPos[0], initialPos[1], initialPos[2]);
      orbit = [
        pos,
        Cesium.Cartesian3.fromDegrees(
          (180 - Math.abs(initialPos[0])) * (initialPos[0] < 0 ? 1 : -1) + latOffset,
          -initialPos[1] + longOffset,
          initialPos[2]
        ),
        new Cesium.Cartesian3.fromDegrees(
          (180 - Math.abs(initialPos[0])) * (initialPos[0] < 0 ? 1 : -1) - latOffset,
          -initialPos[1] - longOffset,
          initialPos[2]
        ),
        pos,
      ];
    }

    return [initialPos, orbit, path];
  }, [agent]);

  return (
    <Entity
      name={agent.name}
      position={Cesium.Cartesian3.fromDegrees(...pos)}
      point={{
        pixelSize: config.pixelSize,
        color: config.colors.primary,
        show: !agentTemplate?.model,
      }}
      model={
        agentTemplate?.model && {
          uri: agentTemplate.model._root.cadSignedUrl,
          scale:
            agentTemplate.model._root.cadScaleFactor > 1
              ? (cesiumScaleLinearSlope * 1000 + cesiumScaleLinearIntercept) *
                agentTemplate.model._root.cadScaleFactor
              : 1000 * cesiumModelScaleDown * agentTemplate.model._root.cadScaleFactor * 3000,
          show: true,
        }
      }
    >
      {orbit && (
        <PolylineGraphics
          positions={Math.random() > 0.5 ? orbit.reverse() : orbit}
          show={true}
          width={config.width}
          material={
            new Cesium.StripeMaterialProperty({
              evenColor: config.colors.tail,
              oddColor: Cesium.Color.TRANSPARENT,
              repeat: 2,
              offset: new Cesium.CallbackProperty(
                () =>
                  (viewerRef.current.cesiumElement._clockViewModel.systemTime.secondsOfDay * 0.75) %
                  2,
                false
              ),
              orientation: Cesium.StripeOrientation.VERTICAL,
            })
          }
          arcType={Cesium.ArcType.GEODESIC}
        />
      )}
      {path && (
        <>
          <PolylineGraphics
            positions={path}
            show={true}
            width={config.width}
            material={config.colors.path}
            arcType={Cesium.ArcType.GEODESIC}
          />
          <PointPrimitiveCollection show={true}>
            {path.map((pos, i) => (
              <PointPrimitive
                key={i}
                position={pos}
                pixelSize={config.pixelSize / 3}
                color={config.colors.primary}
              />
            ))}
          </PointPrimitiveCollection>
        </>
      )}
      <LabelGraphics
        text={agent.name}
        show={true}
        scale={0.4}
        fillColor={config.colors.primary}
        pixelOffset={Cesium.Cartesian2.fromElements(15, 15, new Cesium.Cartesian2())}
      />
    </Entity>
  );
};

const ScenarioThumbnail = ({ branch }) => {
  const model = useModel(branch.model);
  const viewerRef = useRef(null);
  return (
    <Viewer
      ref={viewerRef}
      style={{
        height: '100%',
        width: '100%',
      }}
      timeline={false}
      animation={false}
      shouldAnimate={false}
      baseLayerPicker={false}
      sceneModePicker={false}
      scene3DOnly={true}
      infoBox={false}
      selectionIndicator={false}
      homeButton={false}
      geocoder={false}
      fullscreenButton={false}
      navigationHelpButton={false}
      msaaSamples={4}
    >
      <ScreenSpaceCameraController enableLook={false} enableZoom={true} />
      <SkyAtmosphere show={true} />
      <SkyBox show={true} />
      <ShadowMap darkness={0} />
      <Camera />
      <Globe
        dynamicAtmosphereLighting={true}
        dynamicAtmosphereLightingFromSun={true}
        baseColor={Cesium.Color.SKYBLUE}
        enableLighting={true}
        depthTestAgainstTerrain={false}
      />
      <Sun />
      <Scene />
      {_.sampleSize(
        model.Agent.all().filter((agent) => configMap[agent.type]),
        5
      ).map((agent) => (
        <ScenarioAgent key={agent.id} agent={agent} viewerRef={viewerRef} />
      ))}
    </Viewer>
  );
};

export default memo(ScenarioThumbnail);
